import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect, Suspense } from "react";
import { lazyWithRetry as lazy } from "@/lib/lazyWithRetry";
import { supabase } from "@/integrations/supabase/client";
import Index from "./pages/Index";
import { MaintenanceGate } from "@/components/MaintenanceGate";
import { LegalConsentToast } from "@/components/LegalConsentToast";
import { RainbetLinkPrompt } from "@/components/RainbetLinkPrompt";

const Auth = lazy(() => import("./pages/Auth"));
const Profile = lazy(() => import("./pages/Profile"));
const Admin = lazy(() => import("./pages/Admin"));
const StoreAdmin = lazy(() => import("./pages/StoreAdmin"));
const Store = lazy(() => import("./pages/Store"));
const Giveaways = lazy(() => import("./pages/Giveaways"));
const BalanceGuess = lazy(() => import("./pages/BalanceGuess"));
const PromoCodes = lazy(() => import("./pages/PromoCodes"));
const DiscordCallback = lazy(() => import("./pages/DiscordCallback"));
const KickCallback = lazy(() => import("./pages/KickCallback"));
const Tiers = lazy(() => import("./pages/Tiers"));
const Challenges = lazy(() => import("./pages/Challenges"));
const DailyLogin = lazy(() => import("./pages/DailyLogin"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const Milestones = lazy(() => import("./pages/Milestones"));
const HypedropLeaderboard = lazy(() => import("./pages/HypedropLeaderboard"));
const RainbetLeaderboard = lazy(() => import("./pages/RainbetLeaderboard"));
const Leaderboards = lazy(() => import("./pages/Leaderboards"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const NotFound = lazy(() => import("./pages/NotFound"));
const News = lazy(() => import("./pages/News"));
const About = lazy(() => import("./pages/About"));
const SponsoredBattles = lazy(() => import("./pages/SponsoredBattles"));
const RainbetGuide = lazy(() => import("./pages/RainbetGuide"));
const CalendarPage = lazy(() => import("./pages/Calendar"));
const SlotRequests = lazy(() => import("./pages/SlotRequests"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const AdminTts = lazy(() => import("./pages/AdminTts"));
const ObsTts = lazy(() => import("./pages/ObsTts"));
const ObsMedia = lazy(() => import("./pages/ObsMedia"));
const ObsWager = lazy(() => import("./pages/ObsWager"));
const ObsGiveaway = lazy(() => import("./pages/ObsGiveaway"));

const queryClient = new QueryClient();

// Builds a lightweight device fingerprint from browser-available signals.
function collectFingerprint() {
  const ua = navigator.userAgent;

  // OS detection
  const os = /Windows/i.test(ua) ? 'Windows'
    : /Mac OS X/i.test(ua) ? 'macOS'
    : /Android/i.test(ua) ? 'Android'
    : /iPhone|iPad|iPod/i.test(ua) ? 'iOS'
    : /Linux/i.test(ua) ? 'Linux'
    : 'Unknown';

  // Browser detection
  const browser = /Edg\//i.test(ua) ? 'Edge'
    : /OPR\//i.test(ua) ? 'Opera'
    : /Chrome\//i.test(ua) ? 'Chrome'
    : /Firefox\//i.test(ua) ? 'Firefox'
    : /Safari\//i.test(ua) ? 'Safari'
    : 'Unknown';

  // Device type
  const deviceType = /Mobi|Android/i.test(ua) && !/iPad/i.test(ua) ? 'Mobile'
    : /iPad|Tablet/i.test(ua) ? 'Tablet'
    : 'Desktop';

  const screenResolution = `${screen.width}x${screen.height}`;
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const language = navigator.language;

  // Simple stable hash of the key signals
  const raw = `${ua}|${screenResolution}|${timezone}|${language}`;
  let hash = 0;
  for (let i = 0; i < raw.length; i++) {
    hash = (Math.imul(31, hash) + raw.charCodeAt(i)) | 0;
  }
  const hashStr = (hash >>> 0).toString(16).padStart(8, '0');

  return { hash: hashStr, userAgent: ua, browser, os, deviceType, screenResolution, timezone, language };
}

// Silently captures the user's IP + device fingerprint on every authenticated session.
function IpTracker() {
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        const fingerprint = collectFingerprint();
        supabase.functions.invoke('track-ip', { body: { fingerprint } }).catch(() => {});
      }
    });
    return () => subscription.unsubscribe();
  }, []);
  return null;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <BrowserRouter>
        <Routes>
          <Route
            path="/obs/tts"
            element={
              <Suspense fallback={<div style={{ width: '100vw', height: '100vh', background: 'transparent' }} />}>
                <ObsTts />
              </Suspense>
            }
          />
          <Route
            path="/obs/media"
            element={
              <Suspense fallback={<div style={{ width: '100vw', height: '100vh', background: 'transparent' }} />}>
                <ObsMedia />
              </Suspense>
            }
          />
          <Route
            path="/obs/giveaway"
            element={
              <Suspense fallback={<div style={{ width: '100vw', height: '100vh', background: 'transparent' }} />}>
                <ObsGiveaway />
              </Suspense>
            }
          />
          <Route
            path="/obs/wager"
            element={
              <Suspense fallback={<div style={{ width: '100vw', height: '100vh', background: 'transparent' }} />}>
                <ObsWager />
              </Suspense>
            }
          />
          <Route
            path="*"
            element={
              <Suspense fallback={<div className="min-h-screen bg-background" />}>
                <MainApp />
              </Suspense>
            }
          />
        </Routes>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

const MainApp = () => (
  <>
    <Toaster />
    <Sonner />
    <IpTracker />
    <LegalConsentToast />
    <RainbetLinkPrompt />
    <MaintenanceGate>
      <Routes>
            <Route path="/" element={<Index />} />
            
            <Route path="/giveaways" element={<Giveaways />} />
            <Route path="/tiers" element={<Tiers />} />
            <Route path="/challenges" element={<Challenges />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/auth/discord/callback" element={<DiscordCallback />} />
            <Route path="/auth/kick/callback" element={<KickCallback />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/admin/tts" element={<AdminTts />} />
            <Route path="/store-admin" element={<StoreAdmin />} />
            <Route path="/store" element={<Store />} />
            <Route path="/balance-guess" element={<BalanceGuess />} />
            <Route path="/promo-codes" element={<PromoCodes />} />
            <Route path="/daily-login" element={<DailyLogin />} />
            <Route path="/support" element={<FAQPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/milestones" element={<Milestones />} />
            <Route path="/leaderboards" element={<Leaderboards />} />
            <Route path="/hypedrop-leaderboard" element={<HypedropLeaderboard />} />
            <Route path="/rainbet-leaderboard" element={<RainbetLeaderboard />} />
            <Route path="/onboarding" element={<Onboarding />} />
            <Route path="/news" element={<News />} />
            <Route path="/news/:id" element={<News />} />
            <Route path="/about" element={<About />} />
            <Route path="/sponsored-battles" element={<SponsoredBattles />} />
            <Route path="/rainbet-guide" element={<RainbetGuide />} />
            <Route path="/gamdom-guide" element={<Navigate to="/rainbet-guide" replace />} />
            <Route path="/calendar" element={<CalendarPage />} />
            <Route path="/slot-requests" element={<SlotRequests />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
    </MaintenanceGate>
  </>
);

export default App;
